import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { AuthTokenService } from '@sondermind/host-auth';
import { Logger } from '@sondermind/datadog-logging';
import {
  AiChatClientService,
  AiChatService,
  AI_CHAT_CONFIG,
  AI_CHAT_UI_CONFIG,
  mergeAgentConfig,
  MESSAGE_KIND,
  type AiChatConfig,
  type Message,
} from '@sondermind/ai-chat-angular';

import { W9AgentPanelHeaderComponent, type W9AgentTab } from '../w9-agent-panel-header/w9-agent-panel-header.component';
import { W9AgentMessageRowComponent } from '../w9-agent-message-row/w9-agent-message-row.component';
import { W9DeskBriefWelcomeComponent } from '../w9-desk-brief-welcome/w9-desk-brief-welcome.component';
import { W9TaskListComponent } from '../w9-task-list/w9-task-list.component';
import { W9AgentComposerComponent } from '../w9-agent-composer/w9-agent-composer.component';

/**
 * Builds the W-9-review-scoped AiChat config from runtime configuration.
 * Used as a useFactory so baseUrl is read at runtime (not module load).
 * `agentServiceUrl` is supplied per-environment in the member-portal config
 * JSON files; when it is absent (e.g. ephemeral environments, whose generated
 * config does not emit the key) we derive it from `eeNamespace`, matching the
 * client-portal fallback. Fail fast only if neither is available.
 */
function w9ReviewChatConfigFactory(config: ConfigurationService): AiChatConfig {
  const agentServiceUrl = config.env?.agentServiceUrl
    || (config.env?.eeNamespace ? `https://agentsvc-${config.env.eeNamespace}.sondermind.biz` : undefined);
  if (!agentServiceUrl) {
    throw new Error('agentServiceUrl is not configured; cannot start the W-9 review chat.');
  }
  return {
    baseUrl: agentServiceUrl,
    agentType: 'w9-collection',
    isVoiceEnabled: false,
    deferUntilAuth: true,
  };
}

@Component({
  selector: 'member-w9-agent-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    W9AgentPanelHeaderComponent,
    W9AgentMessageRowComponent,
    W9DeskBriefWelcomeComponent,
    W9TaskListComponent,
    W9AgentComposerComponent,
  ],
  styleUrls: ['./w9-agent-panel.component.scss'],
  templateUrl: './w9-agent-panel.component.html',
  // Component-scoped providers isolate this panel's chat client from the
  // app-global (customer-support) one, mirroring W9ChatPageComponent.
  providers: [
    {
      provide: AI_CHAT_CONFIG,
      useFactory: w9ReviewChatConfigFactory,
      deps: [ConfigurationService],
    },
    {
      provide: AI_CHAT_UI_CONFIG,
      useValue: mergeAgentConfig(undefined),
    },
    AiChatClientService,
    AiChatService,
  ],
})
export class W9AgentPanelComponent implements OnInit {
  @Input() providerId = '';
  @Input() providerFirstName = '';
  @Input() tinType = 'ssn';

  @Output() readonly fieldsUpdated = new EventEmitter<void>();

  @ViewChild('scrollEl') scrollEl?: ElementRef<HTMLElement>;

  private readonly aiChat = inject(AiChatService);
  private readonly chatClient = inject(AiChatClientService);
  private readonly tokenService = inject(AuthTokenService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeTab = signal<W9AgentTab>('chat');
  readonly input = signal('');
  readonly sendError = signal(false);

  readonly messages = this.aiChat.messages;

  readonly visibleMessages = computed(() => this.messages().filter((m: Message) => {
    if (m.role !== 'assistant') return true;
    // Keep the typing indicator; drop assistant turns with no rendered text.
    if (m.status === 'assistant_pending') return true;
    return !!m.text && m.text.trim().length > 0;
  }));

  readonly hasUserMessage = computed(() => this.messages().some((m: Message) => m.role === 'user'));

  readonly providerInitials = computed(() => (this.hasUserMessage() ? 'ME' : '?'));

  private started = false;
  private lastW9UpdateCount = 0;

  constructor() {
    effect(() => {
      this.messages();
      queueMicrotask(() => {
        const el = this.scrollEl?.nativeElement;
        if (el) el.scrollTop = el.scrollHeight;
      });
    });

    effect(() => {
      const w9UpdateCount = this.messages().filter(
        (m: Message) => m.meta?.['messageKind'] === MESSAGE_KIND.W9FieldsUpdated
      ).length;
      if (w9UpdateCount > this.lastW9UpdateCount) {
        this.fieldsUpdated.emit();
      }
      this.lastW9UpdateCount = w9UpdateCount;
    });
  }

  ngOnInit(): void {
    // Feed the Auth0 token into the route-scoped chat client (deferUntilAuth: true).
    this.tokenService
      .getAccessToken$()
      .pipe(
        filter((token): token is string => !!token),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((token) => this.chatClient.setAuthToken(token));
  }

  onTabChange(tab: W9AgentTab): void { this.activeTab.set(tab) }

  onSend(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!this.aiChat.ready()) {
      this.logger.debug('W9AgentPanel: send before client ready, ignoring');
      return;
    }
    this.sendError.set(false);
    this.input.set('');
    this.dispatch(trimmed).catch(() => {
      // dispatch handles its own errors; this guards against floating promises.
    });
  }

  private async dispatch(text: string): Promise<void> {
    try {
      if (!this.started) {
        this.started = true;
        // mode: 'review' seeds the w9-collection thread into review mode so the
        // backend routes to the review_session Q&A/edit loop instead of replaying
        // the end-of-collection CTA.
        await this.aiChat.startConversation({
          userRef: this.providerId,
          initialState: { mode: 'review' },
        });
      }
      await this.aiChat.send(text);
    } catch (err) {
      this.logger.error('W9AgentPanel: failed to send message', { err });
      this.sendError.set(true);
    }
  }
}
